'use client'

import { GroupObjects } from '@carbon/icons-react'
import { TrashIcon } from '@heroicons/react/20/solid'
import type { Node, NodeProps, ReactFlowState } from '@xyflow/react'
import {
  NodeResizer,
  NodeToolbar,
  useReactFlow,
  useStore,
  useStoreApi,
} from '@xyflow/react'
import { Button } from 'app/_components/Button'
import { memo } from 'react'
import { useReadOnly } from 'slate-react'
import { DEFAULT_NODE_BACKGROUND } from '../constants'
import useDetachNodes from '../hooks/useDetachNodes'
import { useUpdateNodeData } from '../hooks/useUpdateElementData'
import { getRelativeNodesBounds } from '../lib/bounds'
import { ColorSelect } from './ColorSelect'
import { FlowToolbar } from './FlowToolbar'
import { NodeHandles } from './NodeHandles'

export type GroupNodeType = Node<{
  backgroundColor?: string
}>

type GroupBounds = {
  minWidth: number
  minHeight: number
  hasChildNodes: boolean
}

const getChildNodes = (state: ReactFlowState, parentId: string) =>
  Array.from(state.parentLookup.get(parentId)?.values() ?? [])

function GroupNode({ id, data, selected }: NodeProps<GroupNodeType>) {
  const { deleteElements } = useReactFlow()
  const store = useStoreApi()
  const isEditable = !useReadOnly()
  const detachNodes = useDetachNodes()
  const updateNodeData = useUpdateNodeData<GroupNodeType['data']>(id)

  const { minWidth, minHeight, hasChildNodes } = useStore<GroupBounds>(
    (state: ReactFlowState) => {
      const childNodes = getChildNodes(state, id)
      const { x, y, width, height } = getRelativeNodesBounds(childNodes)

      return {
        minWidth: x + width,
        minHeight: y + height,
        hasChildNodes: childNodes.length > 0,
      }
    },
    (prev, next) =>
      prev.minWidth === next.minWidth &&
      prev.minHeight === next.minHeight &&
      prev.hasChildNodes === next.hasChildNodes,
  )

  const onDelete = () => {
    deleteElements({ nodes: [{ id }] })
  }

  const onDetach = () => {
    const childIds = getChildNodes(store.getState(), id).map((node) => node.id)

    detachNodes(childIds, id)
  }

  return (
    <div
      className="absolute inset-0"
      style={{
        backgroundColor: `${data.backgroundColor || DEFAULT_NODE_BACKGROUND}aa`,
      }} // aa is the opacity
    >
      <NodeHandles isVisible={isEditable} />
      <NodeResizer
        isVisible={isEditable && selected}
        minHeight={minHeight}
        minWidth={minWidth}
      />
      <NodeToolbar isVisible={isEditable && selected} className="nodrag">
        <FlowToolbar>
          <ColorSelect
            value={data.backgroundColor}
            onChange={(value) => updateNodeData({ backgroundColor: value })}
            colorOptions={{
              Pink: '#facce0',
              Hellgrün: '#d9f399',
              Blau: '#f3f5fa',
              Gelb: '#ffffe6',
              Custom: 'custom',
            }}
          />

          <Button
            aria-label="Löschen"
            shape="icon"
            variant="ghost"
            onClick={onDelete}
            title="Löschen"
          >
            <TrashIcon />
          </Button>

          {hasChildNodes && (
            <Button
              aria-label="Gruppierung aufheben"
              shape="icon"
              variant="ghost"
              onClick={onDetach}
              title="Gruppierung aufheben"
            >
              <GroupObjects />
            </Button>
          )}
        </FlowToolbar>
      </NodeToolbar>
    </div>
  )
}

export default memo(GroupNode)
